import { HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  AccountDataService,
  ADD_BEIHILFE_LEGACY_HEADER_TOKEN,
  AntragDtoModel,
  AntragsFlag,
  AntragsType,
  ApiService,
  ConfigSettingsService,
  ConverterService,
  CredentialError,
  CredentialsService,
  CryptoProvider,
  DeviceOrigin,
  Guid,
} from '@ebeihilfe/core';
import { EinreichungApiService as SharedEinreichungApi } from '@ebeihilfe/einreichung-shared/services';
import { firstValueFrom, Observable } from 'rxjs';

import { Antrag } from '../types';

@Injectable({
  providedIn: 'root',
})
export class EinreichungApiService {
  private readonly API_URL = 'data/';
  private readonly legacyContext = new HttpContext().set(ADD_BEIHILFE_LEGACY_HEADER_TOKEN, true);

  private readonly accountData = inject(AccountDataService);
  private readonly api = inject(ApiService);
  private readonly configService = inject(ConfigSettingsService);
  private readonly converter = inject(ConverterService);
  private readonly credentialsSevice = inject(CredentialsService);
  private readonly crypto = inject(CryptoProvider);
  private readonly sharedEinreichungApi = inject(SharedEinreichungApi);

  public async createAntrag(deviceOrigin: DeviceOrigin, antrag: Antrag): Promise<void> {
    if (antrag.antragsId && antrag.timestamp) {
      this.sharedEinreichungApi.deleteAntrag(antrag.antragsId.toString()).subscribe();
    }

    const [userSecret, pinResult, salts] = await Promise.all([
      this.credentialsSevice.getUserSecret(),
      this.credentialsSevice.requestPin(),
      this.accountData.getSalts(),
    ]);

    if (!pinResult.success) {
      throw new CredentialError('PIN');
    }

    if (!userSecret) {
      throw new Error('userSecret can not be null');
    }

    const pinSalt = salts.pinSalt || new Uint8Array();
    const passwordHash = this.converter.uIntArrayToBase64(
      await this.crypto.computeHash(userSecret, salts.passwordSalt),
    );
    const pinHash = pinResult.hasPin
      ? this.converter.uIntArrayToBase64(await this.crypto.computeHash(pinResult.pin, pinSalt))
      : null;

    if (!passwordHash) {
      throw new Error('passwordHash or pinHash can not be null');
    }

    const flags = antrag.getFlags();
    let antragsArt = antrag.antragsType;

    if (antrag.antragsType === AntragsType.UNDEFINED) {
      antragsArt = AntragsType.KURZANTRAG;
      console.warn('AntragsType was UNDEFINED, setting to KURZANTRAG as default.');
    }

    if (antragsArt === AntragsType.LANGANTRAG && !this.configService.appSettings.einreichungV3Activated) {
      throw Error('langantrag is not possible with old einreichung');
    }

    const payload: {
      passwordHash: string;
      pinHash: string | null;
      deviceOrigin: DeviceOrigin;
      flags: AntragsFlag[];
      antragsArt: AntragsType;
      containerId?: string;
    } = {
      passwordHash,
      pinHash,
      deviceOrigin,
      flags,
      antragsArt,
    };

    if (this.configService.appSettings.einreichungV3Activated && antrag.containerId !== '') {
      payload.containerId = antrag.containerId;
    }

    const { antragsId, concurrency } = await firstValueFrom(
      this.api.post<{ antragsId: string; concurrency: string }>(`${this.API_URL}antraege`, payload, {
        context: payload.containerId
          ? new HttpContext().set(ADD_BEIHILFE_LEGACY_HEADER_TOKEN, false)
          : this.legacyContext,
      }),
    );
    antrag.setDataAfterCreationOrReset(Guid.create(antragsId), concurrency);
  }

  /**
   * Gibt ein Array aus unterbrochen Anträgen wurde.
   * @returns Einen Observable welches einen Array aus Anträgen emitted der angibt ob der Antrag unterbrochen wurde.
   * @throws Einen Fehler wenn das Backend nicht erreichbar ist.
   */
  public getUnfinishedAntraege(): Observable<AntragDtoModel[]> {
    return this.api.restGet(`${this.API_URL}antraege/unfinished`, { context: this.legacyContext });
  }
}
